import { useSession } from '@/providers/session-provider';
import type { ModelName } from '@pigello/pigello-matrix';
import { useLocalStorage } from 'usehooks-ts';

interface RecentSearch {
  [organizationId: string]: Array<{
    id: string;
    modelName: ModelName;
  }>;
}

const RECENT_SEARCHES_KEY = 'recent-searches';

export function useRecentSearches() {
  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const [recentSearches, setRecentSearches] = useLocalStorage<RecentSearch>(
    RECENT_SEARCHES_KEY,
    { [organizationId]: [] }
  );

  const addRecentSearch = (id: string, modelName: ModelName) => {
    setRecentSearches((prev) => {
      // Ensure that the search is not duplicated and there's only five recent searches. Remove the last if there are more than five
      const existing = prev[organizationId] ?? [];
      const filtered = existing.filter((instance) => id !== instance.id);
      const trimmed = filtered.slice(0, 4); // Keep only the last 4 searches
      return {
        ...prev,
        [organizationId]: [
          {
            id,
            modelName,
          },
          ...trimmed,
        ],
      };
    });
  };

  const removeRecentSearch = (id: string) => {
    setRecentSearches((prev) => ({
      ...prev,
      [organizationId]: prev[organizationId]?.filter((s) => s.id !== id) ?? [],
    }));
  };

  const clearRecentSearches = () => {
    setRecentSearches((prev) => ({
      ...prev,
      [organizationId]: [],
    }));
  };

  function placeRecentSearchAtTop(id: string, modelName: ModelName) {
    setRecentSearches((prev) => {
      const existing = prev[organizationId] ?? [];
      return {
        ...prev,
        [organizationId]: [
          { id, modelName },
          ...existing.filter((s) => s.id !== id),
        ],
      };
    });
  }

  return {
    recentSearches: recentSearches[organizationId] ?? [],
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
    placeRecentSearchAtTop,
  };
}
